import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import measurementService from '../../../services/measurementService';
import { getMeasurementErrorMessage } from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { determinePeriodFromTime, getInstantPeriodOptions, PERMANENT_START_TIMES } from '../../../utils/measurementPeriodUtils';
import AddFormModal from '../../../components/AddFormModal/AddFormModal';
import './AddBGForm.css';

const AddBGForm = ({ isOpen, onClose, practiceId, patientId, onSuccess, initialScheduleTargets = null }) => {
  const getEmptyFormData = () => ({
    date: '',
    time: '',
    period: '',
    blood_glucose_value: '',
    note: ''
  });

  const [loading, setLoading] = useState(false);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [isAutoPeriod, setIsAutoPeriod] = useState(true);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(getEmptyFormData());

  useEffect(() => {
    if (!isOpen || !practiceId) return;
    setIsAutoPeriod(true);
    setErrors({});
    setFormData(getEmptyFormData());
    if (initialScheduleTargets && typeof initialScheduleTargets === 'object') {
      setScheduleTargets(initialScheduleTargets);
    } else {
      fetchScheduleTargets();
    }
  }, [isOpen, practiceId, patientId, initialScheduleTargets]);

  useEffect(() => {
    if (scheduleTargets && isAutoPeriod && formData.time) {
      const determined = determinePeriodFromTime(
        formData.time,
        scheduleTargets?.bloodGlucoseTimePeriod || [],
        PERMANENT_START_TIMES.bloodGlucose
      );
      setFormData((p) => ({ ...p, period: determined || '' }));
    }
  }, [scheduleTargets, isAutoPeriod, formData.time]);

  const fetchScheduleTargets = async () => {
    try {
      const effectiveTargets = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(effectiveTargets);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const resolvedPeriod = isAutoPeriod
      ? (
        determinePeriodFromTime(
          formData.time,
          scheduleTargets?.bloodGlucoseTimePeriod || [],
          PERMANENT_START_TIMES.bloodGlucose
        ) || ''
      )
      : formData.period;

    if (isAutoPeriod && resolvedPeriod !== formData.period) {
      setFormData((prev) => ({ ...prev, period: resolvedPeriod }));
    }

    const newErrors = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!resolvedPeriod) newErrors.period = 'Period is required';
    if (!formData.blood_glucose_value) newErrors.blood_glucose_value = 'Blood glucose is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setLoading(true);
    try {
      const selectedPeriod = uniquePeriods.find((p) => p.name === formData.period);
      const periodId = selectedPeriod?.id ?? null;
      await measurementService.createBloodGlucoseMeasurement(practiceId, patientId, {
        measurement_date: formData.date,
        measurement_time: formData.time,
        period_id: periodId,
        period_name: resolvedPeriod,
        blood_glucose_value: parseFloat(formData.blood_glucose_value),
        note: formData.note || null
      });
      toast.success('Blood glucose measurement added successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(getMeasurementErrorMessage(error, 'Failed to add measurement'));
    } finally {
      setLoading(false);
    }
  };

  const uniquePeriods = useMemo(
    () => getInstantPeriodOptions(scheduleTargets?.bloodGlucoseTimePeriod || [], PERMANENT_START_TIMES.bloodGlucose),
    [scheduleTargets]
  );

  const handleTimeChange = (timeValue) => {
    setFormData((prev) => {
      if (!isAutoPeriod) {
        return { ...prev, time: timeValue };
      }
      const determined = determinePeriodFromTime(
        timeValue,
        scheduleTargets?.bloodGlucoseTimePeriod || [],
        PERMANENT_START_TIMES.bloodGlucose
      ) || '';
      return { ...prev, time: timeValue, period: determined };
    });
  };

  return (
    <AddFormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Add Blood Glucose"
      footer={(
        <div className="add-practice-form-actions">
          <button type="button" className="add-practice-btn add-practice-btn-cancel" onClick={onClose} disabled={loading}>Close</button>
          <button type="submit" className="add-practice-btn add-practice-btn-add" disabled={loading}>{loading ? 'Saving...' : 'Add'}</button>
        </div>
      )}
    >
      <section className="abg-section">
        <h6 className="abg-section-title">Blood Glucose</h6>
        <div className="abg-grid">
          <div className="abg-field"><label>Date <span className="abg-req">*</span></label><input type="date" value={formData.date} onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))} />{errors.date && <div className="abg-error">{errors.date}</div>}</div>
          <div className="abg-field"><label>Time <span className="abg-req">*</span></label><input type="time" value={formData.time} onChange={(e) => handleTimeChange(e.target.value)} />{errors.time && <div className="abg-error">{errors.time}</div>}</div>
          <div className="abg-field"><label>Period <span className="abg-req">*</span></label><select value={isAutoPeriod ? 'AUTO' : formData.period} onChange={(e) => { const v = e.target.value; if (v === 'AUTO') { setIsAutoPeriod(true); setFormData((p) => ({ ...p, period: determinePeriodFromTime(p.time, scheduleTargets?.bloodGlucoseTimePeriod || [], PERMANENT_START_TIMES.bloodGlucose) || '' })); } else { setIsAutoPeriod(false); setFormData((p) => ({ ...p, period: v })); } }}><option value="AUTO">Automatically determine Measurement Period</option>{uniquePeriods.map((p) => <option key={p.id || p.name} value={p.name}>{p.name}</option>)}</select>{errors.period && <div className="abg-error">{errors.period}</div>}</div>
          <div className="abg-field"><label>Blood Glucose <span className="abg-req">*</span></label><input type="number" step="0.1" value={formData.blood_glucose_value} onChange={(e) => setFormData((p) => ({ ...p, blood_glucose_value: e.target.value }))} />{errors.blood_glucose_value && <div className="abg-error">{errors.blood_glucose_value}</div>}</div>
        </div>
        <div className="abg-field"><label>Note</label><textarea rows="2" value={formData.note} onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))} /></div>
      </section>
    </AddFormModal>
  );
};

export default AddBGForm;
